import React from 'react';

import { UxContext } from '@client/context/UxContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { ChannelMapEntry } from '@ledium/types';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper } from '@mui/material';

import ZoneWizardStep1 from './ZoneWizardStep1';
import ZoneWizardStep2 from './ZoneWizardStep2';
import ZoneWizardStep3 from './ZoneWizardStep3';
import ZoneWizardStep4, { type ZoneChannel } from './ZoneWizardStep4';
import ZoneWizardStep5 from './ZoneWizardStep5';

interface ZoneWizardProps {
    sourceZoneId: string;
    onClose: () => void;
    onDone: (newZoneId: string) => void;
    initialScenario?: ZoneWizardState['scenario'];
}

interface ZoneWizardState {
    activeStep: 0 | 1 | 2 | 3 | 4;
    scenario: 'copy' | 'link-sync' | 'link-independent';
    channelMap: Record<string, number | undefined>;
    newName: string;
    sourceZoneName: string;
    existingNames: string[];
    channels: ZoneChannel[];
    isSubmitting: boolean;
}

type ZoneWizardAction =
    | { type: 'SET_STEP'; step: 0 | 1 | 2 | 3 | 4 }
    | { type: 'SET_SCENARIO'; scenario: ZoneWizardState['scenario'] }
    | { type: 'SET_CHANNEL_MAP'; channelMap: Record<string, number | undefined> }
    | { type: 'SET_NEW_NAME'; name: string }
    | { type: 'SET_ZONE_DATA'; sourceZoneName: string; existingNames: string[] }
    | { type: 'SET_CHANNELS'; channels: ZoneChannel[] }
    | { type: 'SET_SUBMITTING'; isSubmitting: boolean };

function wizardReducer(state: ZoneWizardState, action: ZoneWizardAction): ZoneWizardState {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, activeStep: action.step };
        case 'SET_SCENARIO':
            return { ...state, scenario: action.scenario };
        case 'SET_CHANNEL_MAP':
            return { ...state, channelMap: action.channelMap };
        case 'SET_NEW_NAME':
            return { ...state, newName: action.name };
        case 'SET_ZONE_DATA':
            return { ...state, sourceZoneName: action.sourceZoneName, existingNames: action.existingNames };
        case 'SET_CHANNELS':
            return { ...state, channels: action.channels };
        case 'SET_SUBMITTING':
            return { ...state, isSubmitting: action.isSubmitting };
        default:
            return state;
    }
}

function isNextEnabled(state: ZoneWizardState): boolean {
    if (state.activeStep === 2) {
        const trimmed = state.newName.trim();
        return trimmed.length > 0 && !state.existingNames.includes(trimmed);
    }
    if (state.activeStep === 3) {
        return Object.values(state.channelMap).every((v) => v !== undefined && v >= 1 && v <= 512);
    }
    return true;
}

function buildChannelMapArray(map: Record<string, number | undefined>, channels: ZoneChannel[]): ChannelMapEntry[] {
    return channels.filter((c) => map[c.id] !== undefined).map((c) => ({ sourceChannelId: c.id, dmxAddress: map[c.id] as number, resolution: c.resolution }));
}

function ZoneWizard({ sourceZoneId, onClose, onDone, initialScenario }: ZoneWizardProps) {
    const { handleAlert, handleSnackbar } = React.useContext(UxContext)!;

    const [state, dispatch] = React.useReducer(wizardReducer, {
        activeStep: 0,
        scenario: initialScenario ?? 'copy',
        channelMap: {},
        newName: '',
        sourceZoneName: '',
        existingNames: [],
        channels: [],
        isSubmitting: false
    });

    React.useEffect(() => {
        lediumApi.get<Array<{ id: string; name: string }>>('zones').then((data) => {
            if (!data) {
                return;
            }
            const source = data.find((z) => z.id === sourceZoneId);
            dispatch({
                type: 'SET_ZONE_DATA',
                sourceZoneName: source?.name ?? '',
                existingNames: data.filter((z) => z.id !== 'root').map((z) => z.name)
            });
        });
    }, []);

    const stepLabels = [
        i18n('copy_zone_step1_label') as string,
        i18n('copy_zone_step2_label') as string,
        i18n('copy_zone_step3_label') as string,
        i18n('copy_zone_step4_label') as string,
        i18n('copy_zone_step5_label') as string
    ];

    function handleCloseRequest() {
        if (state.activeStep === 0) {
            onClose();
            return;
        }
        handleAlert({
            title: i18n('discard_section_changes_title') as string,
            message: i18n('discard_section_changes_message') as string,
            okCallback: onClose
        });
    }

    function handleNext() {
        dispatch({ type: 'SET_STEP', step: (state.activeStep + 1) as 0 | 1 | 2 | 3 | 4 });
    }

    function handleBack() {
        dispatch({ type: 'SET_STEP', step: (state.activeStep - 1) as 0 | 1 | 2 | 3 | 4 });
    }

    async function handleSubmit() {
        dispatch({ type: 'SET_SUBMITTING', isSubmitting: true });
        const channelMap = buildChannelMapArray(state.channelMap, state.channels);
        if (state.scenario === 'link-sync' || state.scenario === 'link-independent') {
            const result = await lediumApi.post(`zones/${sourceZoneId}/links`, {
                type: state.scenario === 'link-independent' ? 'independent' : 'mirror',
                name: state.newName,
                channelMap
            });
            dispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
            if (result) {
                handleSnackbar({ message: i18n('copy_zone_link_success') as string });
                onDone('');
                onClose();
            }
        } else {
            const result = await lediumApi.post<{ zoneId: string }>(`zones/${sourceZoneId}/copy`, {
                name: state.newName,
                channelMap
            });
            dispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
            if (result) {
                handleSnackbar({ message: i18n('copy_zone_success') as string });
                onDone(result.zoneId);
                onClose();
            }
        }
    }

    const confirmLabel = state.scenario === 'copy' ? i18n('copy_zone_confirm') : i18n('copy_zone_confirm_link');

    return (
        <Dialog open fullWidth maxWidth="md">
            <DialogTitle sx={{ pr: 6 }}>
                {i18n('copy_zone_wizard_title')}
                <IconButton onClick={handleCloseRequest} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={state.activeStep} sx={{ mb: 3, mt: 1 }}>
                    {stepLabels.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {state.activeStep === 0 && <ZoneWizardStep1 />}
                {state.activeStep === 1 && <ZoneWizardStep2 scenario={state.scenario} onChange={(scenario) => dispatch({ type: 'SET_SCENARIO', scenario })} />}
                {state.activeStep === 2 && (
                    <ZoneWizardStep3
                        sourceZoneName={state.sourceZoneName}
                        value={state.newName}
                        onChange={(name) => dispatch({ type: 'SET_NEW_NAME', name })}
                        existingNames={state.existingNames}
                        scenario={state.scenario}
                    />
                )}
                {state.activeStep === 3 && (
                    <ZoneWizardStep4
                        zoneId={sourceZoneId}
                        channelMap={state.channelMap}
                        onChannelMapChange={(channelMap) => dispatch({ type: 'SET_CHANNEL_MAP', channelMap })}
                        scenario={state.scenario}
                        onChannelsLoaded={(channels) => dispatch({ type: 'SET_CHANNELS', channels })}
                    />
                )}
                {state.activeStep === 4 && (
                    <ZoneWizardStep5 channelMap={state.channelMap} newName={state.newName} scenario={state.scenario} channels={state.channels} />
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button color="secondary" onClick={handleCloseRequest}>
                    {i18n('cancel')}
                </Button>
                <Box>
                    <Button disabled={state.activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                        {i18n('back')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isNextEnabled(state) || state.isSubmitting}
                        onClick={state.activeStep === 4 ? handleSubmit : handleNext}
                    >
                        {state.activeStep === 4 ? confirmLabel : i18n('next')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default ZoneWizard;
